import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';

interface FireworksCanvasProps {
  width?: number;
  height?: number;
  active?: boolean;
}

class Firework {
  scene: THREE.Scene;
  done: boolean = false;
  dest: THREE.Vector3[] = [];
  geometry: THREE.BufferGeometry | null = null;
  points: THREE.Points | null = null;
  group: THREE.Group | null = null;
  material: THREE.PointsMaterial;
  positions: Float32Array | null = null;
  colors: Float32Array | null = null;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.material = new THREE.PointsMaterial({
      size: 16,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      depthTest: false,
    });

    this.launch();
  }

  reset() {
    if (this.group) {
      this.scene.remove(this.group);
    }
    if (this.points) {
      this.geometry?.dispose();
      this.material.dispose();
    }
    this.dest = [];
    this.geometry = null;
    this.points = null;
    this.group = null;
    this.positions = null;
    this.colors = null;
  }

  launch() {
    const x = 0;
    const y = -200;
    const z = -400;

    const from = new THREE.Vector3(x, y, z);
    const to = new THREE.Vector3(x, 150, z);

    this.dest = [to];

    const color = new THREE.Color(1, 0, 0);

    this.positions = new Float32Array([from.x, from.y, from.z]);
    this.colors = new Float32Array([color.r, color.g, color.b]);

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(this.colors, 3));

    this.points = new THREE.Points(this.geometry, this.material);
    this.scene.add(this.points);
  }

  explode(origin: THREE.Vector3) {
    if (this.points) {
      this.scene.remove(this.points);
      this.geometry?.dispose();
    }

    const count = 200;
    this.dest = [];

    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);

    const scale = 6;
    const zDepth = 200;

    for (let i = 0; i < count; i++) {
      const index = i * 3;

      const t = (i / count) * Math.PI * 2;

      const x = scale * 16 * Math.pow(Math.sin(t), 3);
      const y = -scale * (13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t));
      const z = THREE.MathUtils.randFloatSpread(zDepth);

      const from = origin.clone().add(new THREE.Vector3(
        THREE.MathUtils.randFloatSpread(10),
        THREE.MathUtils.randFloatSpread(10),
        THREE.MathUtils.randFloatSpread(10)
      ));

      const to = origin.clone().add(new THREE.Vector3(x, y, z));

      const color = new THREE.Color(1, 0, 0);

      positions[index] = from.x;
      positions[index + 1] = from.y;
      positions[index + 2] = from.z;

      colors[index] = color.r;
      colors[index + 1] = color.g;
      colors[index + 2] = color.b;

      this.dest.push(to);
    }

    this.positions = positions;
    this.colors = colors;

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    this.points = new THREE.Points(this.geometry, this.material);

    this.group = new THREE.Group();
    this.group.add(this.points);
    this.scene.add(this.group);
  }

  update() {
    if (!this.points || !this.geometry || !this.positions) return;

    const count = this.dest.length;
    const positionAttr = this.geometry.getAttribute('position') as THREE.BufferAttribute;

    for (let i = 0; i < count; i++) {
      const index = i * 3;
      const v = new THREE.Vector3(
        positionAttr.array[index],
        positionAttr.array[index + 1],
        positionAttr.array[index + 2]
      );

      v.x += (this.dest[i].x - v.x) / 20;
      v.y += (this.dest[i].y - v.y) / 20;
      v.z += (this.dest[i].z - v.z) / 20;

      this.positions[index] = v.x;
      this.positions[index + 1] = v.y;
      this.positions[index + 2] = v.z;
    }

    positionAttr.needsUpdate = true;

    if (count === 1 && this.positions[1] >= this.dest[0].y - 5) {
      this.explode(new THREE.Vector3(this.positions[0], this.positions[1], this.positions[2]));
    }

    if (count > 1) {
      this.material.opacity -= 0.015;
    }

    if (this.material.opacity <= 0) {
      this.reset();
      this.done = true;
    }
  }
}

const FireworksCanvas: React.FC<FireworksCanvasProps> = ({
  width = window.innerWidth,
  height = window.innerHeight,
  active = true,
}) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const fireworkRef = useRef<Firework | null>(null);
  const animationIdRef = useRef<number>(0);

  useEffect(() => {
    if (!active) return;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setClearColor(0x000000, 0);

    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 20000);
    camera.position.set(0, 0, 500);

    const scene = new THREE.Scene();
    mountRef.current?.appendChild(renderer.domElement);

    // Only one firework
    fireworkRef.current = new Firework(scene);

    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);

      if (fireworkRef.current && !fireworkRef.current.done) {
        fireworkRef.current.update();
      }

      renderer.render(scene, camera);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationIdRef.current);
      if (mountRef.current) {
        mountRef.current.removeChild(renderer.domElement);
      }
    };
  }, [active, width, height]);

  return (
    <div
      ref={mountRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: `${width}px`,
        height: `${height}px`,
        overflow: 'hidden',
        zIndex: 9999,
        pointerEvents: 'none',
      }}
    />
  );
};

export default FireworksCanvas;
